import { motion } from 'framer-motion';
import { CheckCircle, Circle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ProgressAnimationProps {
  percent: number;
  stage: string;
  onCancel: () => void;
}

const STAGE_LABELS = {
  started: 'Initializing...',
  connecting: 'Connecting to portal',
  authenticating: 'Authenticating credentials',
  accessing_accounts: 'Accessing account list',
  retrieving_bills: 'Retrieving latest bills',
  parsing: 'Parsing & preparing results',
  completed: 'Completed',
} as const;

const LONG_WAIT_MESSAGES = [
  'Portal testing can take 1-3 minutes...',
  'Please be patient while we test your credentials...',
  'This is normal for portal bot testing...',
  'We are securely testing your portal access...',
];

const STAGE_BULLETS = [
  { percent: 15, label: 'Connecting to portal', stage: 'connecting' },
  { percent: 35, label: 'Authenticating credentials', stage: 'authenticating' },
  { percent: 55, label: 'Accessing account list', stage: 'accessing_accounts' },
  { percent: 75, label: 'Retrieving latest bills', stage: 'retrieving_bills' },
  { percent: 90, label: 'Parsing & preparing results', stage: 'parsing' },
];

export function ProgressAnimation({ percent, stage, onCancel }: ProgressAnimationProps) {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  
  // Show long wait message if progress is slow
  const showLongWaitMessage = percent < 30 && stage === 'connecting';
  const longWaitMessage = LONG_WAIT_MESSAGES[Math.floor(Date.now() / 10000) % LONG_WAIT_MESSAGES.length];

  return (
    <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-blue-50/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-xl">
          <span className="text-2xl">⚡</span>
          Testing Connection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Circular Progress */}
        <div className="flex justify-center">
          <div className="relative w-32 h-32">
            <svg className="transform -rotate-90 w-32 h-32">
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-blue-200"
              />
              {/* Progress circle */}
              <motion.circle
                cx="64"
                cy="64"
                r={radius}
                stroke="url(#gradient)"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                initial={{ strokeDashoffset: circumference }}
                animate={{ strokeDashoffset }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />
              {/* Gradient definition */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="50%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#06B6D4" />
                </linearGradient>
              </defs>
            </svg>
            {/* Percentage text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{percent}%</span>
            </div>
          </div>
        </div>

        {/* Stage description */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {STAGE_LABELS[stage as keyof typeof STAGE_LABELS] || 'Processing...'}
          </p>
          {showLongWaitMessage && (
            <p className="text-xs text-amber-600 mt-2 animate-pulse">
              {longWaitMessage}
            </p>
          )}
        </div>

        {/* Stage bullets */}
        <div className="space-y-3">
          {STAGE_BULLETS.map((bullet) => {
            const isCompleted = percent > bullet.percent;
            const isCurrent = percent === bullet.percent || (percent > bullet.percent && percent < (STAGE_BULLETS[STAGE_BULLETS.indexOf(bullet) + 1]?.percent || 100));
            
            return (
              <div key={bullet.stage} className="flex items-center gap-3">
                {isCompleted ? (
                  <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                ) : isCurrent ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                <span className={`text-sm ${isCompleted ? 'text-foreground' : isCurrent ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  {bullet.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Cancel button */}
        <Button
          variant="outline"
          className="w-full hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors"
          onClick={onCancel}
          disabled={percent >= 100}
        >
          <X className="mr-2 h-4 w-4" />
          Cancel Test
        </Button>
      </CardContent>
    </Card>
  );
}
